import React, { useMemo } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';
import clsx from 'clsx';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMusic } from '@fortawesome/pro-light-svg-icons/faMusic';
import { faChartLine } from '@fortawesome/pro-light-svg-icons/faChartLine';
import { faLink } from '@fortawesome/pro-light-svg-icons/faLink';
import { faWandMagicSparkles } from '@fortawesome/pro-light-svg-icons/faWandMagicSparkles';
import { faCalendarDays } from '@fortawesome/pro-light-svg-icons/faCalendarDays';
import { faPenToSquare } from '@fortawesome/pro-light-svg-icons/faPenToSquare';
import { faLock } from '@fortawesome/pro-light-svg-icons/faLock';
import { faPhotoFilm } from '@fortawesome/pro-light-svg-icons/faPhotoFilm';
import { faUsers } from '@fortawesome/pro-light-svg-icons/faUsers';
import { faCompactDisc } from '@fortawesome/pro-light-svg-icons/faCompactDisc';
import { faShareNodes } from '@fortawesome/pro-light-svg-icons/faShareNodes';
import { faGear } from '@fortawesome/pro-light-svg-icons/faGear';
import { faPaperPlane } from '@fortawesome/pro-light-svg-icons/faPaperPlane';
import { faInbox } from '@fortawesome/pro-light-svg-icons/faInbox';
import { TypedMemo } from '../../common/types';
import { oauthConsentMessages as messages } from './oauthConsentMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole consent page; each part uses its own slice of it */
import styles from './OAuthConsentView.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PermissionRow {
  icon: IconDefinition;
  text: MessageDescriptor;
  isOff?: boolean;
}

const SEE_ROWS: PermissionRow[] = [
  { icon: faMusic, text: messages.seeCatalog },
  { icon: faChartLine, text: messages.seeNumbers },
  { icon: faLink, text: messages.seeLinks },
  { icon: faPhotoFilm, text: messages.seeWork },
  { icon: faUsers, text: messages.seeWorkspace },
  { icon: faInbox, text: messages.seeOutreach },
];

const DO_ROWS: PermissionRow[] = [
  { icon: faWandMagicSparkles, text: messages.doAssets },
  { icon: faCalendarDays, text: messages.doPosts },
  { icon: faPenToSquare, text: messages.doLinks },
  { icon: faCompactDisc, text: messages.doCatalog },
  { icon: faShareNodes, text: messages.doShare },
  { icon: faGear, text: messages.doSettings },
  { icon: faPaperPlane, text: messages.doOutreach },
];

const READ_ONLY_ROWS: PermissionRow[] = [{ icon: faLock, text: messages.doNothing, isOff: true }];

interface OAuthConsentPermissionsProps {
  clientName: string;
  canWrite: boolean;
}

/**
 * What the app can see and what it can do (design: oauth-consent a1 and b1, `.oc-perms`). The
 * "can see" rows stand for `music24:read`, the "can do" rows for `music24:write`. A read-only
 * request has one grey row in "can do". The rows cover every area of the MCP tool list
 * (`mcpTools.generated.json`): when a tool area comes or goes, change the rows too.
 */
const OAuthConsentPermissionsComponent: React.FC<OAuthConsentPermissionsProps> = ({ clientName, canWrite }) => {
  const intl = useIntl();
  const groups = useMemo(
    () => [
      { key: 'see', label: messages.canSee, rows: SEE_ROWS },
      { key: 'do', label: messages.canDo, rows: canWrite ? DO_ROWS : READ_ONLY_ROWS },
    ],
    [canWrite]
  );
  return (
    <div className={styles.permissions}>
      {groups.map((group) => (
        <section key={group.key} className={styles.group}>
          <div className={styles.label}>{intl.formatMessage(group.label, { app: clientName })}</div>
          <ul className={styles.rows}>
            {group.rows.map((row) => (
              <li key={row.text.id} className={clsx(styles.row, row.isOff === true && styles.rowOff)}>
                <span className={styles.rowIcon} aria-hidden="true">
                  <FontAwesomeIcon icon={row.icon} />
                </span>
                <span>{intl.formatMessage(row.text)}</span>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
};

export const OAuthConsentPermissions = TypedMemo(OAuthConsentPermissionsComponent);
